"use client";
import { useCompositeItem } from "../composite/composite-item.js";
import { useRadioContext } from "./radio-context.js";
import { createElement, createHook, forwardRef, memo, useEvent, useForceUpdate, useId, useMergeRefs, useTagName } from "@ariakit/react-utils";
import { disabledFromProps, removeUndefinedValues } from "@ariakit/utils";
import { useEffect, useRef } from "react";
import { useStoreState } from "@ariakit/react-store";
//#region src/radio/radio.tsx
const TagName = "input";
function getIsChecked(value, storeValue) {
	if (storeValue === void 0) return;
	if (value != null && storeValue != null) return storeValue === value;
	return !!storeValue;
}
function isNativeRadio(tagName, type) {
	return tagName === "input" && (!type || type === "radio");
}
/**
* Returns props to create a `Radio` component.
* @see https://ariakit.com/components/radio
* @example
* ```jsx
* const store = useRadioStore();
* const props = useRadio({ store, value: "Apple" });
* <RadioGroup store={store}>
*   <Role {...props} render={<input />} />
*   <Radio value="Orange" />
* </RadioGroup>
* ```
*/
const useRadio = createHook(function useRadio({ store, name: nameProp, value, checked, ...props }) {
	const context = useRadioContext();
	store = store || context;
	const id = useId(props.id);
	const ref = useRef(null);
	const isChecked = useStoreState(store, (state) => checked ?? getIsChecked(value, state?.value));
	const storeId = useStoreState(store, "id");
	const name = nameProp ?? storeId;
	useEffect(() => {
		if (!id) return;
		if (!isChecked) return;
		if (store?.getState().activeId === id) return;
		store?.setActiveId(id);
	}, [
		store,
		isChecked,
		id
	]);
	const onChangeProp = props.onChange;
	const nativeRadio = isNativeRadio(useTagName(ref, TagName), props.type);
	const disabled = disabledFromProps(props);
	const [propertyUpdated, schedulePropertyUpdate] = useForceUpdate();
	useEffect(() => {
		const element = ref.current;
		if (!element) return;
		if (nativeRadio) return;
		if (isChecked !== void 0) element.checked = isChecked;
		if (name !== void 0) element.name = name;
		if (value !== void 0) element.value = `${value}`;
	}, [
		propertyUpdated,
		nativeRadio,
		isChecked,
		name,
		value
	]);
	const onChange = useEvent((event) => {
		if (disabled) {
			event.preventDefault();
			event.stopPropagation();
			return;
		}
		if (store?.getState().value === value) return;
		if (!nativeRadio) {
			event.currentTarget.checked = true;
			schedulePropertyUpdate();
		}
		onChangeProp?.(event);
		if (event.defaultPrevented) return;
		store?.setValue(value);
	});
	const onClickProp = props.onClick;
	const onClick = useEvent((event) => {
		onClickProp?.(event);
		if (event.defaultPrevented) return;
		if (nativeRadio) return;
		onChange(event);
	});
	const onFocusProp = props.onFocus;
	const onFocus = useEvent((event) => {
		onFocusProp?.(event);
		if (event.defaultPrevented) return;
		if (!nativeRadio) return;
		if (disabled) return;
		if (!store) return;
		const { moves, activeId } = store.getState();
		if (!moves) return;
		if (id && activeId !== id) return;
		if (event.currentTarget.checked) return;
		event.currentTarget.click();
	});
	props = {
		role: !nativeRadio ? "radio" : void 0,
		type: nativeRadio ? "radio" : void 0,
		"aria-checked": isChecked,
		...props,
		id,
		ref: useMergeRefs(ref, props.ref),
		onChange,
		onClick,
		onFocus
	};
	props = useCompositeItem({
		store,
		clickOnEnter: !nativeRadio,
		...props
	});
	return removeUndefinedValues({
		name: nativeRadio ? name : void 0,
		value: nativeRadio ? value : void 0,
		checked: isChecked,
		...props
	});
});
/**
* Renders a radio button element that's typically wrapped in a
* [`RadioGroup`](https://ariakit.com/reference/radio-group) component.
* @see https://ariakit.com/components/radio
* @example
* ```jsx {3-4}
* <RadioProvider>
*   <RadioGroup>
*     <Radio value="Apple" />
*     <Radio value="Orange" />
*   </RadioGroup>
* </RadioProvider>
* ```
*/
const Radio = memo(forwardRef(function Radio(props) {
	return createElement(TagName, useRadio(props));
}));
//#endregion
export { Radio, useRadio };

//# sourceMappingURL=radio.js.map